import { useState, RefObject, useRef, MouseEvent, Dispatch } from 'react';
import { AlertColor, Button, TableRow, TableCell, useMediaQuery, useTheme } from '@mui/material';
import strings from '../../../../../../common/Translation/Translate';
import Form, { FormDataModel } from '../../../../../../components/Form';
import Notification from '../../../../../../components/Notification';
import { AddEditHighDemandDateForm } from './AddEditHighDemandDateForm';
import DrawerMenu from '../../../../../../components/DrawerMenu';
import { removeErrorFieldsFromValues } from '../../../../../../utils/validators';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import dayjs from 'dayjs';
import Tabular from '../../../../../../components/Tabular/Tabular';
import { ActionColumn, FormComponent } from '../../../../../../utils/validators/HelperComponents';
import { getDrawerWidth } from 'utils/validators/HelperFunctions';

type AccordianComponentProps = {
  editedData: any;
  editRow: (idx: any) => void;
  deleteRow: (idx: any) => void;
};
const AccordianComponent = ({ editedData, editRow, deleteRow }: AccordianComponentProps) => {
  return (
    <Tabular
      rows={[
        strings.bookingDetailsStartDate,
        strings.bookingDetailsEndDate,
        strings.venueConfig_general_highDemandName,
        strings.customermgmt_column_action,
      ]}
      sx={{ 'thead > tr, tbody > tr': { whiteSpace: 'nowrap' } }}>
      {editedData.length > 0 &&
        editedData?.map((row: any, index: number) => (
          <TableRow key={editedData.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
            <TableCell scope="row">{row.highDemandStartDate}</TableCell>
            <TableCell scope="row">{row.highDemandEndDate}</TableCell>
            <TableCell scope="row">{row.highDemandCategory || '-'}</TableCell>
            <TableCell scope="row">
              <ActionColumn editRow={editRow} deleteRow={deleteRow} index={index} />
            </TableCell>
          </TableRow>
        ))}
    </Tabular>
  );
};

type AddEditHighDemandDateProps = {
  editedData: any;
  setData: Dispatch<any>;
};

export const AddEditHighDemandDate: React.FC<AddEditHighDemandDateProps> = ({ editedData = [], setData }) => {
  const theme = useTheme();
  const screenSizeDownSm = useMediaQuery(theme.breakpoints.down('sm'));
  const screenSizeDownMd = useMediaQuery(theme.breakpoints.down('md'));
  const screenSizeUpLg = useMediaQuery(theme.breakpoints.up('lg'));
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [pageResponse, setPageResponse] = useState<any>({});
  const [hasError, setHasError] = useState<boolean>(false);
  const [selectedRowId, setSelectedRowId] = useState<any>(null);
  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: string;
  }>({
    display: false,
    severity: null,
    message: '',
  });

  let formRef: RefObject<Form | null | undefined> = useRef();

  const closeNotification = (value: boolean) => {
    setMessage({ ...message, display: value });
  };

  const handleSave = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const { getFormData } = formRef.current as {
      getFormData: () => { formData: FormDataModel; isFormValid: boolean };
    };
    const { formData, isFormValid } = getFormData();
    const data = removeErrorFieldsFromValues(formData);
    setHasError(false);
    if (isFormValid) {
      if (dayjs(data.highDemandEndDate as string).isSameOrAfter(dayjs(data.highDemandStartDate as string))) {
        const array = [...editedData];
        const formattedData = {
          ...data,
          highDemandStartDate: dayjs(data.highDemandStartDate as string).format('DD-MM-YYYY'),
          highDemandEndDate: dayjs(data.highDemandEndDate as string).format('DD-MM-YYYY'),
        };
        if (selectedRowId !== null) {
          array[selectedRowId] = { ...array[selectedRowId], ...formattedData };
          setSelectedRowId(null);
        } else {
          array.push(formattedData);
        }
        setData(array);
        setOpenDrawer(false);
        setPageResponse({});
      } else {
        setMessage({
          display: true,
          severity: 'warning',
          message: strings.common_endDate_error_message,
        });
      }
    } else {
      setHasError(true);
    }
  };

  const handleClose = () => {
    setOpenDrawer(false);
    setPageResponse({});
    formRef.current?.resetForm();
    setSelectedRowId(null);
    setHasError(false);
  };



  const editRow = (idx: any) => {
    setSelectedRowId(idx);
    const editRow = JSON.parse(JSON.stringify(editedData));
    const editValue = editRow.find((item: any, i: any) => i === idx);
    setPageResponse({
      ...editValue,
      highDemandStartDate: Date.parse(editValue.highDemandStartDate.split('-').reverse().join('-')),
      highDemandEndDate: Date.parse(editValue.highDemandEndDate.split('-').reverse().join('-')),
    });
    setOpenDrawer(true);
  };

  const deleteRow = (idx: any) => {
    const deleteRow = JSON.parse(JSON.stringify(editedData));
    const deleteValue = deleteRow.filter((item: any, i: any) => i !== idx);
    setData(deleteValue);
  };


  return (
    <>
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
          autoHideDuration={2000}
        />
      )}
      <Button
        onClick={() => setOpenDrawer(true)}
        variant="text"
        sx={{ '.MuiButton-endIcon': { marginLeft: '8px' }, minHeight: '36px', my: 1 }}
        endIcon={<AddCircleOutlineIcon />}>
        {strings.high_demand_header}
      </Button>
      {editedData?.length ? (
        <AccordianComponent editedData={editedData} editRow={editRow} deleteRow={deleteRow} />
      ) : null}
      <DrawerMenu
        width={getDrawerWidth(screenSizeDownSm, screenSizeDownMd, screenSizeUpLg)}
        open={openDrawer}
        handleClose={handleClose}
        closeButton={true}
        title={strings.high_demand_header}>
        <FormComponent
          hasError={hasError}
          formRef={formRef}
          pageResponse={pageResponse}
          handleSave={handleSave}
          formModel={AddEditHighDemandDateForm(strings)}
        />
      </DrawerMenu>
    </>
  );
};
